/**
 * TTS settings: thin layer over central settings (settings/index.ts).
 * Keeps the same API (getStored*, setStored*) for features/tts.ts.
 * Defaults and storage keys are defined in settings/defaults and settings/storage.
 */

import { getSetting, setSetting, getRawLocal, TTS_DEFAULTS, HIGHLIGHT_COLORS } from '../settings';

export const PREDEFINED_HIGHLIGHT_COLORS = HIGHLIGHT_COLORS;

export function getStoredVoice(): string | null {
  return getSetting('tts', 'voice') as string | null;
}

export function setStoredVoice(voiceUri: string | null): void {
  setSetting('tts', 'voice', voiceUri);
}

export function getStoredRate(): number {
  return getSetting('tts', 'rate') as number;
}

export function setStoredRate(rate: number): void {
  setSetting('tts', 'rate', rate);
}

export function getStoredVolume(): number {
  return getSetting('tts', 'volume') as number;
}

export function setStoredVolume(vol: number): void {
  setSetting('tts', 'volume', vol);
}

/** Lang: from storage when user has set it, else <html lang>, else default (English). */
export function getStoredLang(): string {
  const raw = getRawLocal('tts', 'lang');
  if (raw != null && raw.trim() !== '') return raw.trim().toLowerCase().substring(0, 2);
  const html = typeof document !== 'undefined' ? document.documentElement?.getAttribute?.('lang') ?? '' : '';
  return html && html.length >= 2 ? html.toLowerCase().substring(0, 2) : TTS_DEFAULTS.lang;
}

export function setStoredLang(lang: string): void {
  setSetting('tts', 'lang', lang || null);
}

export function getStoredSubtitles(): boolean {
  const raw = getRawLocal('tts', 'subtitles');
  if (raw == null || raw === '') return true;
  return getSetting('tts', 'subtitles') as boolean;
}

export function setStoredSubtitles(on: boolean): void {
  setSetting('tts', 'subtitles', on);
}

export function getStoredAutoAdvance(): boolean {
  return getSetting('tts', 'autoAdvance') as boolean;
}

export function setStoredAutoAdvance(on: boolean): void {
  setSetting('tts', 'autoAdvance', on);
}

export function getStoredHighlightColorId(): string {
  const id = getSetting('tts', 'highlightColorId') as string;
  return HIGHLIGHT_COLORS.some((c) => c.id === id) ? id : TTS_DEFAULTS.highlightColorId;
}

export function getStoredHighlightRgb(): string {
  const id = getStoredHighlightColorId();
  const found = HIGHLIGHT_COLORS.find((c) => c.id === id);
  return found ? found.rgb : HIGHLIGHT_COLORS[0].rgb;
}

export function setStoredHighlightColor(colorId: string | null): void {
  setSetting('tts', 'highlightColorId', colorId);
}

/** Source language for notes (for translation). data-notes-lang on .reveal or #lectura-root, else <html lang>. */
export function getNotesSourceLang(): string {
  try {
    const reveal = document.querySelector('.reveal');
    if (reveal) {
      const override = reveal.getAttribute('data-notes-lang');
      if (override && override.length >= 2) return override.toLowerCase().substring(0, 2);
    }
    const root = document.getElementById('lectura-root');
    if (root) {
      const override = root.getAttribute('data-notes-lang');
      if (override && override.length >= 2) return override.toLowerCase().substring(0, 2);
    }
    const html = document.documentElement?.getAttribute?.('lang') ?? '';
    return html && html.length >= 2 ? html.toLowerCase().substring(0, 2) : TTS_DEFAULTS.lang;
  } catch {
    return TTS_DEFAULTS.lang;
  }
}
